import { useEffect, useMemo, useState } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { useContext } from "react";
import { UserDetailsContext } from "App";
import MessagesAPI from "api/messages/MessageAPI";
import { messageConfig } from "message-framework/commonMessageConfig";
import { conditionResolver } from "message-framework/conditionResolver";
import { componentRegistry } from "message-framework/componentRegistry";

const getDeletedMessageIdsForSession = () => {
  return (
    JSON.parse(sessionStorage.getItem("updates-tabs-dismissed-message-ids")) ||
    []
  );
};

const getRoutePath = (route) => route.path || route.url || route.from;

const routeMatches = (route, pathname) => {
  const path = getRoutePath(route);
  if (!path) return false;

  const end =
    route.type === "redirect" || route.exact === true || !path.includes("*");
  return Boolean(matchPath({ path, end }, pathname));
};

const getRouteScore = (route) => {
  const path = getRoutePath(route) || "";
  const wildcardBonus = path.includes("*") ? 1 : 0;
  return path.replace("*", "").length + wildcardBonus;
};

const findBestRouteMatch = (routes, pathname) => {
  return routes
    .filter((route) => routeMatches(route, pathname))
    .sort((a, b) => getRouteScore(b) - getRouteScore(a))[0];
};
const getCurrentProduct = (pathname) => {
  const [, firstSegment, secondSegment] = pathname.split("/");
  return firstSegment === "app" ? secondSegment : firstSegment;
};

export function useModuleAlerts(routesList) {
  const location = useLocation();
  const userDetails = useContext(UserDetailsContext);
  const [apiResults, setApiResults] = useState({});

  // Derive current scopeId — subScopes take priority over parent scope
  const scopeId = useMemo(() => {
    const routes = routesList.filter((r) => r.key);

    // check sub-pages first for most specific match
    for (const route of routes) {
      const subPages = route.subPages || route.subScopes;
      if (subPages?.length) {
        const subMatch = findBestRouteMatch(subPages, location.pathname);
        if (subMatch) {
          //console.log(subMatch.key)
          return subMatch.key;
        }
      }
    }

    // fall back to parent route scope
    const match = findBestRouteMatch(routes, location.pathname);
    return match?.key ?? null;
  }, [location.pathname, routesList]);

  // Derive the parent scopeId regardless of subScope match
  // used to inherit parent-scoped alerts into sub-pages
  const parentScopeId = useMemo(() => {
    const match = findBestRouteMatch(
      routesList.filter((r) => r.key),
      location.pathname,
    );
    return match?.key ?? null;
  }, [location.pathname, routesList]);

  // Fire API calls for API-driven alerts scoped to current page
  useEffect(() => {
    if (!scopeId) return;

    const deletedMessageIdsForSession = getDeletedMessageIdsForSession();

    const apiAlerts = messageConfig.filter(
      (a) =>
        !deletedMessageIdsForSession.includes(a.id) &&
        a.condition === "API_RESPONSE" &&
        (a.scope.includes("*") ||
          a.scope.includes(scopeId) ||
          a.scope.includes(parentScopeId)),
    );

    apiAlerts.forEach(async (alert) => {
      const response = await MessagesAPI.getMessage(
        alert.apiUrl,
        alert.payload,
        alert.apiMethod,
      );
      const resolved = conditionResolver[alert.apiCondition]?.(response) ?? {};
      const { show, status, ...remaining } = resolved;

      setApiResults((prev) => ({
        ...prev,
        [alert.id]: {
          show: resolved?.show ?? false,
          status: resolved?.status ?? alert.status ?? "warning",
          ...remaining,
        },
      }));
    });
  }, [scopeId, parentScopeId]);

  const currentProduct = getCurrentProduct(location.pathname);

  // Build the messages array UpdatesTabs expects
  const messages = useMemo(() => {
    const effectiveScopes = [
      ...(scopeId ? [scopeId] : []),
      ...(parentScopeId && parentScopeId !== scopeId ? [parentScopeId] : []),
    ];

    if (!effectiveScopes.length) return [];

    return messageConfig
      .filter((alert) => {
        // filtering out message ids that are deleted for the session
        if (getDeletedMessageIdsForSession().includes(alert.id)) return false;

        if (alert.products?.length && !alert.products.includes(currentProduct))
          return false;

        const scopeMatch =
          alert.scope.includes("*") ||
          effectiveScopes.some((s) => alert.scope.includes(s));

        if (!scopeMatch) return false;
        if (alert.excludeScope?.includes(scopeId)) return false;

        if (alert.condition === "API_RESPONSE") {
          return apiResults[alert.id]?.show === true;
        }

        return (
          conditionResolver[alert.condition]?.({ userDetails })?.show ?? false
        );
      })
      .map((alert, originalIndex) => ({
        // ← capture originalIndex before sort
        id: alert.id,
        priority: alert.priority ?? 0,
        originalIndex, // ← preserve messageConfig order for tie-breaking
        status:
          alert.condition === "API_RESPONSE"
            ? (apiResults[alert.id]?.status ?? "warning")
            : alert.status,
        component: (() => {
          const Component = componentRegistry[alert.component];
          if (!Component) return null;

          const props =
            alert.condition === "API_RESPONSE"
              ? { apiResponse: apiResults[alert.id] }
              : {
                  title: alert.title,
                  status: alert.status,
                  text: alert.text,
                };

          return <Component {...props} />;
        })(),
        title: alert.title,
        text: alert.text,
        dismissible: alert.dismissible,
      }))
      .sort((a, b) => {
        if (b.priority !== a.priority) return b.priority - a.priority; // descending priority
        return a.originalIndex - b.originalIndex; // stable order for ties
      })
      .map(({ priority, originalIndex, ...rest }) => rest); // strip internal fields
  }, [scopeId, parentScopeId, userDetails, apiResults, currentProduct]);

  return { messages };
}
